'use memo'

import {
  AgxButton,
  AgxModal,
  AgxModalTitleIcon,
  Icon_AlertCircleOutline,
  AgxSpacer,
  AgxTypography,
  AgxDatePicker,
} from '@urbanx/agx.ui-components2'
import { AgxFileUpload } from '@urbanx/agx-ui-components'
import { deleteFile, getFileLink } from 'services'
import { useSelectedCampaign } from 'hooks'

import { AutopilotTaskDto } from '@urbanx/agx.activities.client'
import { DocumentTypes } from '@urbanx/agx.campaigns.client'
import { useMarkedAsSettled } from './useMarkedAsSettled'

interface Props {
  isOpen: boolean
  setIsOpen: (_value: boolean) => void
  task?: AutopilotTaskDto
  onTasksInvalidated?: () => void
}

const MarkedAsSettled = (props: Props) => {
  const { task, isOpen, setIsOpen, onTasksInvalidated } = props
  const campaignId = task?.campaignId
  const contractId = task?.contractId
  const {
    getAuthToken,
    onUploadFile,
    vendorConfirmationFiles,
    buyerConfirmationFiles,
    onVendorConfirmationChanged,
    onBuyerConfirmationChanged,
    settlementDate,
    setSettlementDate,
    isSubmitting,
    canComplete,
    clearSelection,
    onSaveAndExit,
    onComplete,
  } = useMarkedAsSettled({
    contractId,
    campaignId,
    isOpen,
    dialogOpenChangeHandler: setIsOpen,
    onTasksInvalidated,
  })

  const { campaign: selectedCampaign } = useSelectedCampaign()
  const uploadCampaignId = campaignId || selectedCampaign?.campaignId || ''
  const handleDialogOpenChange = (isDialogOpen: boolean) => {
    if (!isDialogOpen) {
      clearSelection()
    }

    setIsOpen(isDialogOpen)
  }

  return (
    <AgxModal
      type={'single'}
      secondaryTitle={
        <AgxSpacer direction="vertical" gap={'sm'}>
          <AgxModalTitleIcon>
            <Icon_AlertCircleOutline type={'Danger'} height={40} width={40} />
          </AgxModalTitleIcon>
          <AgxTypography id={'title'} type={'body_ui_strong'} size={'medium'}>
            Mark as Settled
          </AgxTypography>
        </AgxSpacer>
      }
      actionButtons={[
        <AgxButton
          key={'btnSaveExit'}
          id={'btnSaveExit'}
          text={'Save & Exit'}
          variant={'hollow'}
          onClick={() => onSaveAndExit()}
          disabled={isSubmitting}
          stretch
        />,
        <AgxButton
          key={'btnConfirm'}
          id={'btnConfirm'}
          text={'Confirm'}
          variant={'primary'}
          disabled={isSubmitting || !canComplete}
          onClick={() => onComplete()}
          stretch
        />,
      ]}
      variant={'withDivider'}
      actionButtonContainerStyle={'withDivider'}
      setIsDialogOpen={handleDialogOpenChange}
      isOpen={isOpen}
      content={
        <AgxSpacer
          direction="vertical"
          gap={'md'}
          padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}
        >
          <AgxDatePicker
            id={'settlementDate'}
            label="Settlement Date"
            required
            optionalText=""
            value={settlementDate}
            onChange={(value) => setSettlementDate(value)}
          ></AgxDatePicker>
          <AgxFileUpload
            id="fileUploadVendor"
            title="Vendor Confirmation"
            documentType={DocumentTypes.VendorSettlementConfirmation}
            buttonText={'Choose file'}
            fileData={vendorConfirmationFiles}
            onValueChanged={onVendorConfirmationChanged}
            getAuthToken={getAuthToken}
            campaignId={uploadCampaignId}
            uploadFile={onUploadFile}
            deleteFile={deleteFile}
            getFileLink={getFileLink}
            extensions={['pdf']}
            multiple={false}
            autoUpload={false}
            required
          />
          <AgxFileUpload
            id="fileUploadBuyer"
            title="Buyer Confirmation"
            documentType={DocumentTypes.BuyerSettlementConfirmation}
            buttonText={'Choose file'}
            fileData={buyerConfirmationFiles}
            onValueChanged={onBuyerConfirmationChanged}
            getAuthToken={getAuthToken}
            campaignId={uploadCampaignId}
            uploadFile={onUploadFile}
            deleteFile={deleteFile}
            getFileLink={getFileLink}
            extensions={['pdf']}
            multiple={false}
            autoUpload={false}
            required
          />
        </AgxSpacer>
      }
    ></AgxModal>
  )
}

export default MarkedAsSettled
